import * as v from 'valibot';

import { logValidationErrorsToSentry } from '#shared';

import { activeSigmetSchema } from '#entities/Weather';

import { EValidationIssueSeverity } from '#shared/api';
import type { ValidationIssue } from '#shared/api';

import {
  aircraftFixSchema,
  caseBufferSchema,
  gradeItems,
  isoTimestamp,
  keyframeShapeSchema,
  toIssue,
} from './mapSnapshotResponse.schema';

import type {
  MapSnapshotAircraftFix,
  MapSnapshotCaseBuffer,
  WeatherKeyframe,
} from './mapSnapshotResponse.schema';

export interface PlaybackAircraftTrack {
  aircraftId: number;
  fixes: MapSnapshotAircraftFix[];
}

export interface PlaybackPackWire {
  window: { from: string; to: string };
  cases: MapSnapshotCaseBuffer[];
  weather: WeatherKeyframe[];
  aircraft: PlaybackAircraftTrack[];
}

export type PlaybackPackValidation =
  { ok: true; value: PlaybackPackWire } | { ok: false; message: string };

// duplicate-ok: shape-only pass over the pack; items are graded separately
const packShapeSchema = v.object({
  window: v.object({
    from: isoTimestamp('window.from'),
    to: isoTimestamp('window.to'),
  }),
  cases: v.array(v.unknown()),
  weather: v.array(v.unknown()),
  aircraft: v.array(v.unknown()),
});

const trackShapeSchema = v.object({
  aircraftId: v.number(),
  track: v.array(v.unknown()),
});

const byTime = (a: string, b: string): number => Date.parse(a) - Date.parse(b);

const readKeyframe = (
  raw: unknown,
  index: number,
  issues: ValidationIssue[],
): WeatherKeyframe | null => {
  const keyframe = v.safeParse(keyframeShapeSchema, raw);
  if (!keyframe.success) {
    issues.push(
      toIssue(
        ['weather', index],
        `Weather keyframe dropped: ${v.summarize(keyframe.issues)}`,
        EValidationIssueSeverity.ERROR,
      ),
    );
    return null;
  }
  const rawSigmets = Array.isArray(keyframe.output.sigmets)
    ? keyframe.output.sigmets
    : Object.values(keyframe.output.sigmets);
  return {
    keyframeAt: keyframe.output.keyframeAt,
    sigmets: gradeItems('sigmets', activeSigmetSchema, rawSigmets, issues),
  };
};

const readTrack = (
  raw: unknown,
  index: number,
  issues: ValidationIssue[],
): PlaybackAircraftTrack | null => {
  const track = v.safeParse(trackShapeSchema, raw);
  if (!track.success) {
    issues.push(
      toIssue(
        ['aircraft', index],
        `Aircraft track dropped: ${v.summarize(track.issues)}`,
        EValidationIssueSeverity.ERROR,
      ),
    );
    return null;
  }
  const { aircraftId } = track.output;
  const fixes = gradeItems(
    'aircraft',
    aircraftFixSchema,
    track.output.track.map(fix => ({ ...(fix as object), aircraftId })),
    issues,
  ).sort((a, b) => byTime(a.locationTimestamp, b.locationTimestamp));
  return { aircraftId, fixes };
};

const failPack = (message: string): PlaybackPackValidation => {
  logValidationErrorsToSentry(
    'mapPlaybackPack',
    [toIssue(['root'], message, EValidationIssueSeverity.ERROR)],
    1,
    1,
  );
  return { ok: false, message };
};

export const validatePlaybackPackResponse = (
  response: unknown,
): PlaybackPackValidation => {
  const shape = v.safeParse(packShapeSchema, response);
  if (!shape.success) {
    return failPack(
      `Playback pack has an unusable shape: ${v.summarize(shape.issues)}`,
    );
  }
  const { window, cases, weather, aircraft } = shape.output;
  const issues: ValidationIssue[] = [];

  const buffers = gradeItems('buffers', caseBufferSchema, cases, issues);
  const keyframes = weather
    .map((raw, index) => readKeyframe(raw, index, issues))
    .filter((keyframe): keyframe is WeatherKeyframe => keyframe !== null)
    .sort((a, b) => byTime(a.keyframeAt, b.keyframeAt));
  const tracks = aircraft
    .map((raw, index) => readTrack(raw, index, issues))
    .filter((track): track is PlaybackAircraftTrack => track !== null);

  const errors = issues.filter(
    issue => issue.severity === EValidationIssueSeverity.ERROR,
  );
  if (errors.length > 0) {
    logValidationErrorsToSentry(
      'mapPlaybackPack',
      errors,
      cases.length + weather.length + aircraft.length,
      errors.length,
    );
  }

  return {
    ok: true,
    value: { window, cases: buffers, weather: keyframes, aircraft: tracks },
  };
};
